import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { RoutePaths } from '../../../../constants';
import { authActions } from '../../../../store/auth/slice';
import { useDispatch } from '../../../../store/utils';
import { CodeConfirmation } from './CodeConfirmation/CodeConfirmation';
import styles from './SecondStep.module.scss';
import { Button, Input, PasswordRequirements } from 'components';
import { appActions } from 'store/app/slice';

type Props = {
  userEmail: string;
};

const schema = yup.object({
  password: yup
    .string()
    .required('Field is empty')
    .matches(/^[A-Za-z0-9!@#$%^&*()_+\-=[\]{}|;:'",.<>?/~]{8,25}$/, {
      message: "The input doesn't match password requirements",
    })
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[!@#$%^&*()_+\-=[\]{}|;:'",.<>?/~]/, 'Password must contain at least one special character'),

  repeatPassword: yup
    .string()
    .required('Field is empty')
    .oneOf([yup.ref('password')], 'Passwords do not match, please ensure they are identical'),
  verificationCode: yup.string().required('Field is empty').length(6, 'Incorrect code.'),
});

export const SecondStep = ({ userEmail }: Props) => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    setError,
  } = useForm({ resolver: yupResolver(schema) });

  const watchedPassword = watch('password') as string | undefined;

  const dispatch = useDispatch();
  console.log(errors);
  const onSubmit = handleSubmit(async (data) => {
    try {
      dispatch(appActions.setAppIsLoading(true));
      await dispatch(
        authActions.confirmResetPassword({
          email: userEmail,
          confirmationCode: data.verificationCode,
          newPassword: data.password,
        }),
      ).unwrap();
      await dispatch(authActions.signIn({ username: userEmail, password: data.password })).unwrap();
      navigate(RoutePaths.BASE_PATH);
    } catch (error: any) {
      if (error?.name === 'CodeMismatchException') {
        setError('verificationCode', { type: '400', message: 'Incorrect code' });
        return;
      }
      setError('repeatPassword', { type: '400', message: error?.message });
    } finally {
      dispatch(appActions.setAppIsLoading(false));
    }
  });

  const resendCode = async () => {
    try {
      await dispatch(authActions.resetPassword(userEmail)).unwrap();
    } catch (error: any) {
      setError('verificationCode', { type: '400', message: error?.message });
    }
  };

  const navigateToLogin = () => {
    navigate('/login');
  };

  return (
    <form className={styles.formContainer}>
      <div className={styles.formContent}>
        <CodeConfirmation
          userEmail={userEmail}
          errorMessage={errors.verificationCode?.message}
          setPin={(code: string) => setValue('verificationCode', code)}
          onResend={resendCode}
        />
        <div className={styles.passwordsContainer}>
          <div>
            <Input
              inputName="password"
              labelText="Password"
              placeholder="Enter your password"
              register={register}
              registerOptions={{ required: true }}
              canBeHidden
              errorMsg={errors.password?.message}
            />
            <PasswordRequirements currentPassword={watchedPassword} />
          </div>
          <Input
            inputName="repeatPassword"
            labelText="Repeat password"
            placeholder="Repeat your password"
            errorMsg={errors.repeatPassword?.message}
            register={register}
            registerOptions={{ required: true }}
            canBeHidden
          />
        </div>
      </div>
      <div className={styles.buttonGroup}>
        <Button onClick={navigateToLogin} className={styles.button} type="secondary" isLink>
          Back to Log in
        </Button>
        <Button onClick={onSubmit} className={styles.button}>
          Confirm
        </Button>
      </div>
    </form>
  );
};
